// src/hooks/queries/attendanceQueries.ts
// ============================================================================
// FIXED: Stable query keys + proper caching to prevent infinite refetch loops
// ============================================================================
// CHANGES:
// 1. Query keys use flat primitives instead of nested objects
// 2. Added refetchOnWindowFocus: false to prevent tab-switch refetches
// 3. Increased staleTime to work with the dashboard's 30s auto-refresh
// 4. Added structuralSharing: true (default but explicit for clarity)
// 5. Memoized filter extraction in useAttendanceRecords
// ============================================================================

import { useQuery, useQueries, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  attendanceService,
  AttendanceFilters,
  AttendanceRecord,
  AttendanceStats,
  LocationStats,
} from '../../services/attendance.service';

// ============================================================================
// QUERY KEY FACTORY — FLAT PRIMITIVES ONLY
// ============================================================================
// ❌ OLD: recordsByFilters: (filters) => ['attendance', 'records', { filters }]
//    → object reference changes every render → infinite refetch
// ✅ NEW: recordsByFilters: (s, e, ps, so) => ['attendance', 'records', s, e, ps, so]
//    → primitives are stable across renders

export const attendanceKeys = {
  all: ['attendance'] as const,

  // Records queries — flat primitive keys
  records: () => [...attendanceKeys.all, 'records'] as const,
  recordsByDate: (date: string) => [...attendanceKeys.records(), date] as const,
  recordsByDateRange: (startDate: string, endDate: string) =>
    [...attendanceKeys.records(), startDate, endDate] as const,
  recordsByFilters: (
    startDate: string,
    endDate: string,
    pageSize: number,
    sortOrder: string
  ) => [...attendanceKeys.records(), startDate, endDate, pageSize, sortOrder] as const,

  // Employee-specific queries
  employee: (employeePin: string) => [...attendanceKeys.all, 'employee', employeePin] as const,
  employeeRecords: (employeePin: string, startDate: string, endDate: string) =>
    [...attendanceKeys.employee(employeePin), 'records', startDate, endDate] as const,
  employeeStats: (employeePin: string, year?: number, month?: number) =>
    [...attendanceKeys.employee(employeePin), 'stats', year ?? 0, month ?? 0] as const,

  // Stats queries
  stats: () => [...attendanceKeys.all, 'stats'] as const,
  statsByDate: (date: string) => [...attendanceKeys.stats(), date] as const,

  // Location queries
  locations: () => [...attendanceKeys.all, 'locations'] as const,
  locationsByDate: (date: string) => [...attendanceKeys.locations(), date] as const,

  // Export queries
  export: () => [...attendanceKeys.all, 'export'] as const,
  exportByDate: (date: string, type: 'all' | 'filtered') =>
    [...attendanceKeys.export(), date, type] as const,
  exportByDateRange: (startDate: string, endDate: string, type: 'all' | 'filtered') =>
    [...attendanceKeys.export(), startDate, endDate, type] as const,
  exportEmployee: (employeePin: string, startDate: string, endDate: string) =>
    [...attendanceKeys.export(), 'employee', employeePin, startDate, endDate] as const,
};

// ============================================================================
// SHARED QUERY DEFAULTS
// ============================================================================
const SHARED_DEFAULTS = {
  refetchOnWindowFocus: false, // Prevents refetch on tab switch
  refetchOnMount: false, // Don't refetch if data exists and isn't stale
  retry: 2, // Retry failed requests twice
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10000),
} as const;

// ============================================================================
// QUERY FUNCTIONS
// ============================================================================

export const fetchAttendanceRecords = async (filters: AttendanceFilters) => {
  console.log('🔍 React Query: Fetching attendance records', filters);

  const { startDate, endDate } = filters;

  if (startDate && endDate && startDate !== endDate) {
    console.log('📅 Fetching date range:', startDate, 'to', endDate);

    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    console.log(`📊 Fetching ${days} days of data...`);

    const allRecords: AttendanceRecord[] = [];

    const promises = [];
    for (let i = 0; i < days; i++) {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      const dateStr = currentDate.toISOString().split('T')[0];

      promises.push(
        attendanceService.getAttendanceRecords({
          ...filters,
          startDate: dateStr,
          endDate: dateStr,
        })
      );
    }

    const results = await Promise.all(promises);

    results.forEach((result) => {
      allRecords.push(...result.records);
    });

    console.log(`✅ React Query: Fetched ${allRecords.length} total records across ${days} days`);

    return {
      records: allRecords,
      hasMore: false,
      totalCount: allRecords.length,
    };
  } else {
    const result = await attendanceService.getAttendanceRecords(filters);
    console.log('✅ React Query: Records fetched', result.records.length);
    return result;
  }
};

export const fetchAttendanceStats = async (date: string) => {
  console.log('📊 React Query: Fetching stats for', date);
  const stats = await attendanceService.getAttendanceStats(date);
  console.log('✅ React Query: Stats fetched', stats);
  return stats;
};

export const fetchLocationStats = async (date: string) => {
  console.log('📍 React Query: Fetching location stats for', date);
  const locations = await attendanceService.getLocationStats(date);
  console.log('✅ React Query: Location stats fetched', locations.length);
  return locations;
};

export const fetchEmployeeAttendance = async (
  employeePin: string,
  startDate: string,
  endDate: string
) => {
  console.log('👤 React Query: Fetching employee attendance', {
    employeePin,
    startDate,
    endDate,
  });
  const records = await attendanceService.getConsolidatedAttendanceForEmployee(
    employeePin,
    startDate,
    endDate
  );
  console.log('✅ React Query: Employee records fetched', records.length);

  return records.map((record, index) => {
    const decimalHours = record.decimalHours || 0;
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    const formattedHours = `${hours}:${minutes.toString().padStart(2, '0')}`;

    return {
      id: record.id || `emp-${employeePin}-${index}`,
      serialNo: index + 1,
      employeeName: record.employeeName || 'Unknown',
      employeeId: record.employeeId || employeePin,
      employeePin: record.employeePin || employeePin,
      date: record.date || '',
      checkIn: record.checkIn || '--',
      checkOut: record.checkOut || '--',
      totalHours: formattedHours,
      decimalHours: decimalHours,
      status: record.status || 'Absent',
      lateMinutes: record.lateMinutes || 0,
      lateRemarks: record.lateRemarks || '',
      location: record.location || 'Unknown',
      normalizedLocation: record.normalizedLocation || 'Unknown',
      avatar: record.avatar || '/images/humanicon.png',
      realImage: record.realImage || '',
      department: record.department || '',
      designation: record.designation || '',
      rawCheckIn: record.rawCheckIn,
      overtimeHours: record.overtimeHours,
      overtimeType: record.overtimeType,
      rawCheckOut: record.rawCheckOut,
      noBreak: record.noBreak,
      noBreakReason: record.noBreakReason,
      noBreakAppliedBy: record.noBreakAppliedBy,
      noBreakAppliedAt: record.noBreakAppliedAt,
      isNightShift: record.isNightShift || false,
      shiftStartTime: record.shiftStartTime || '08:00',
      shiftEndTime: record.shiftEndTime || '18:00',
      allocatedWorkTime: `${record.shiftStartTime || '08:00'} - ${record.shiftEndTime || '18:00'}`,
    } as AttendanceRecord;
  });
};

export const fetchExportData = async (
  startDate: string,
  endDate: string,
  onProgress?: (progress: number) => void
) => {
  console.log('📤 React Query: Fetching export data', { startDate, endDate });

  if (startDate === endDate) {
    return attendanceService.getAllRecordsForExport({
      date: startDate,
      includeDesignation: true,
      onProgress,
    });
  } else {
    const allRecords: AttendanceRecord[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    for (let i = 0; i < days; i++) {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      const dateStr = currentDate.toISOString().split('T')[0];

      const progressPercent = Math.round((i / days) * 100);
      onProgress?.(progressPercent);

      try {
        const dayRecords = await attendanceService.getAllRecordsForExport({
          date: dateStr,
          includeDesignation: true,
        });
        allRecords.push(...dayRecords);
        console.log(`✅ Fetched ${dayRecords.length} records for ${dateStr}`);
      } catch (err) {
        console.warn(`⚠️ Failed to fetch records for ${dateStr}:`, err);
      }
    }

    onProgress?.(100);
    return allRecords;
  }
};

// ============================================================================
// CUSTOM HOOKS — STABLE KEYS + PROPER CACHING
// ============================================================================

/**
 * Hook to fetch attendance records
 * FIX: Extracts primitives from filters object for stable query key
 */
export const useAttendanceRecords = (
  filters: AttendanceFilters,
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof fetchAttendanceRecords>>>,
    'queryKey' | 'queryFn'
  >
) => {
  // ── FIX: Memoize the filters object so queryFn gets a stable reference ──
  const stableFilters = useMemo(
    () => filters,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.startDate, filters.endDate, filters.pageSize, filters.sortOrder]
  );

  return useQuery({
    // ── FIX: Flat primitives instead of nested object ──
    queryKey: attendanceKeys.recordsByFilters(
      filters.startDate || '',
      filters.endDate || '',
      filters.pageSize || 1000,
      filters.sortOrder || 'desc'
    ),
    queryFn: () => fetchAttendanceRecords(stableFilters),
    staleTime: 30 * 1000, // 30 seconds — matches auto-refresh interval
    ...SHARED_DEFAULTS,
    ...options,
  });
};

/**
 * Hook to fetch attendance stats
 */
export const useAttendanceStats = (
  date: string,
  options?: Omit<UseQueryOptions<AttendanceStats>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: attendanceKeys.statsByDate(date),
    queryFn: () => fetchAttendanceStats(date),
    staleTime: 30 * 1000, // 30 seconds
    ...SHARED_DEFAULTS,
    ...options,
  });
};

/**
 * Hook to fetch location stats
 */
export const useLocationStats = (
  date: string,
  options?: Omit<UseQueryOptions<LocationStats[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: attendanceKeys.locationsByDate(date),
    queryFn: () => fetchLocationStats(date),
    staleTime: 60 * 1000, // 1 minute — location data changes less frequently
    ...SHARED_DEFAULTS,
    ...options,
  });
};

/**
 * Hook to fetch employee attendance
 */
export const useEmployeeAttendance = (
  employeePin: string,
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<AttendanceRecord[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: attendanceKeys.employeeRecords(employeePin, startDate, endDate),
    queryFn: () => fetchEmployeeAttendance(employeePin, startDate, endDate),
    enabled: !!employeePin && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
    ...SHARED_DEFAULTS,
    ...options,
  });
};

/**
 * Hook to fetch all data needed for the attendance page
 */
export const useAttendancePageData = (filters: AttendanceFilters, date: string) => {
  const queries = useQueries({
    queries: [
      {
        queryKey: attendanceKeys.recordsByFilters(
          filters.startDate || '',
          filters.endDate || '',
          filters.pageSize || 1000,
          filters.sortOrder || 'desc'
        ),
        queryFn: () => fetchAttendanceRecords(filters),
        staleTime: 30 * 1000,
        ...SHARED_DEFAULTS,
      },
      {
        queryKey: attendanceKeys.statsByDate(date),
        queryFn: () => fetchAttendanceStats(date),
        staleTime: 30 * 1000,
        ...SHARED_DEFAULTS,
      },
      {
        queryKey: attendanceKeys.locationsByDate(date),
        queryFn: () => fetchLocationStats(date),
        staleTime: 60 * 1000,
        ...SHARED_DEFAULTS,
      },
    ],
  });

  return {
    recordsQuery: queries[0],
    statsQuery: queries[1],
    locationsQuery: queries[2],
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    errors: queries.map((q) => q.error).filter(Boolean),
  };
};

/**
 * Hook to fetch export data with progress
 */
export const useExportData = (
  startDate: string,
  endDate: string,
  enabled: boolean = false
) => {
  return useQuery({
    queryKey: attendanceKeys.exportByDateRange(startDate, endDate, 'all'),
    queryFn: () => fetchExportData(startDate, endDate),
    enabled,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    ...SHARED_DEFAULTS,
  });
};